/**
 * <cart-form> in the cart drawer and on the cart page. Every mutation goes
 * through the Cart API with `sections` = every cart-form on the page, and
 * the server-rendered markup replaces each of them, so the drawer and the
 * cart page never disagree and totals, discounts and inventory messages
 * always come from Shopify. Lines are addressed by their key, not their
 * position, and requests run one after another, so quick taps and removals
 * can never hit the wrong line.
 *
 * window.MatterCart: add(items, trigger), addForm(form, trigger),
 * refresh(), open(trigger).
 * Events (document): cart:change {itemCount} after every render,
 *                    cart:add {items, trigger} → adds and opens the drawer.
 */
(() => {
  const allForms = () => Array.from(document.querySelectorAll('cart-form'));
  /* The drawer's form handles global actions (quick add, product forms);
   * the cart page's form is used when there is no drawer. */
  const primaryForm = () => document.querySelector('#CartDrawer cart-form') || document.querySelector('cart-form');
  const sectionIds = () => Array.from(new Set(allForms().map((form) => form.dataset.sectionId).filter(Boolean)));
  let queue = Promise.resolve();
  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  const updateHeader = (source) => {
    const count = Number(source.dataset.cartItemCount || 0);
    const total = source.dataset.cartTotalPrice;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(count);
      if (el.hasAttribute('data-hide-empty')) el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-badge]').forEach((el) => {
      el.textContent = String(count);
    });
    if (total) document.querySelectorAll('[data-cart-total]').forEach((el) => { el.textContent = total; });
    document.querySelectorAll('[data-cart-link]').forEach((el) => el.classList.toggle('has-items', count > 0));
    const label = count === 1 ? source.dataset.labelOne : (source.dataset.labelOther || '').replace('99999', String(count));
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      el.textContent = label;
    });
    return count;
  };

  /* Re-render every cart-form from one Cart API response. */
  const renderAll = (sections, options = {}) => {
    let source = null;
    allForms().forEach((form) => {
      if (form.render(sections, options)) source = source || form;
    });
    if (!source) return;
    const itemCount = updateHeader(source);
    document.dispatchEvent(new CustomEvent('cart:change', { detail: { itemCount } }));
  };

  const parse = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const message = data.description || data.message || `Cart error (${response.status})`;
      throw Object.assign(new Error(message), { data });
    }
    return data;
  };

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionIds().join(','), sections_url: window.location.pathname }),
    }).then(parse);

  const postForm = (url, formData) => {
    formData.append('sections', sectionIds().join(','));
    formData.append('sections_url', window.location.pathname);
    return fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then(parse);
  };

  class CartForm extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.loadRecommendation();
    }

    /* Only the drawer's form opens the drawer; the cart page updates in place. */
    get drawer() {
      return this.closest('dialog-drawer');
    }

    onClick(event) {
      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.change(remove.dataset.key, 0);
        return;
      }
      const step = event.target.closest('[data-cart-qty]');
      if (step) {
        event.preventDefault();
        const input = this.querySelector(`[data-cart-qty-input][data-key="${CSS.escape(step.dataset.key)}"]`);
        const next = Math.max(0, (Number(input?.value) || 0) + Number(step.dataset.cartQty));
        /* Show the new quantity at once so a second tap counts from it. */
        if (input) input.value = String(next);
        this.change(step.dataset.key, next);
        return;
      }
      const add = event.target.closest('[data-cart-add]');
      if (add) {
        event.preventDefault();
        this.add([{ id: Number(add.dataset.variantId), quantity: 1 }], add);
      }
    }

    onChange(event) {
      const input = event.target.closest('[data-cart-qty-input]');
      if (input) {
        this.change(input.dataset.key, Math.max(0, Number(input.value) || 0));
        return;
      }
      const note = event.target.closest('[data-cart-note]');
      if (note) this.updateNote(note.value);
      /* Gift wrapping: the checkbox adds or removes the wrapping product. */
      const wrap = event.target.closest('[data-cart-gift-wrap]');
      if (wrap) {
        if (wrap.checked) this.add([{ id: Number(wrap.dataset.variantId), quantity: 1 }]);
        else if (wrap.dataset.key) this.change(wrap.dataset.key, 0);
      }
    }

    setBusy(busy) {
      allForms().forEach((form) => {
        form.setAttribute('aria-busy', String(busy));
        form.querySelectorAll('[data-cart-checkout]').forEach((button) => {
          button.disabled = busy;
        });
      });
    }

    change(key, quantity) {
      if (!key) return Promise.resolve();
      return enqueue(async () => {
        const input = this.querySelector(`[data-cart-qty-input][data-key="${CSS.escape(key)}"]`);
        const line = input?.dataset.line;
        this.setBusy(true);
        this.clearErrors();
        try {
          const cart = await postJSON(this.dataset.changeUrl, { id: key, quantity });
          renderAll(cart.sections, { focusKey: quantity > 0 ? key : null });
          this.announce(this.dataset.statusUpdated);
        } catch (error) {
          /* Restore the server's quantity, then say why. */
          await this.refreshSilently();
          this.showError(line, error.message);
        } finally {
          this.setBusy(false);
        }
      });
    }

    add(items, trigger, { openOnError = true } = {}) {
      return this.submit(() => postJSON(this.dataset.addUrl, { items }), trigger, openOnError);
    }

    addForm(form, trigger) {
      return this.submit(() => postForm(this.dataset.addUrl, new FormData(form)), trigger, false);
    }

    submit(request, trigger, openOnError) {
      return enqueue(async () => {
        this.setBusy(true);
        trigger?.classList.add('is-loading');
        trigger?.setAttribute('aria-busy', 'true');
        try {
          const data = await request();
          renderAll(data.sections);
          this.announce(this.dataset.statusAdded);
          this.drawer?.open?.(trigger);
          return data;
        } catch (error) {
          if (openOnError) {
            this.showError(null, error.message);
            this.drawer?.open?.(trigger);
          }
          throw error;
        } finally {
          trigger?.classList.remove('is-loading');
          trigger?.removeAttribute('aria-busy');
          this.setBusy(false);
        }
      });
    }

    async updateNote(note) {
      try {
        await postJSON(this.dataset.updateUrl, { note });
      } catch (error) {
        this.showError(null, error.message);
      }
    }

    async refreshSilently() {
      try {
        const response = await fetch(`${window.location.pathname}?sections=${sectionIds().join(',')}`);
        renderAll(await response.json());
      } catch (error) {
        console.warn('Cart refresh failed', error);
      }
    }

    refresh() {
      return enqueue(() => this.refreshSilently());
    }

    /* Replace this form's content from the response; returns true when the
     * response carried this section. */
    render(sections, options = {}) {
      const html = sections?.[this.dataset.sectionId];
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-form');
      if (!fresh) return false;
      const hadFocus = this.contains(document.activeElement);
      // Keep the live accelerated-checkout node: Shopify's script owns it.
      const dynamic = this.querySelector('.cart-foot__dynamic');
      this.innerHTML = fresh.innerHTML;
      const placeholder = this.querySelector('.cart-foot__dynamic');
      if (dynamic && placeholder) placeholder.replaceWith(dynamic);
      this.dataset.cartItemCount = fresh.dataset.cartItemCount || '0';
      this.dataset.cartTotalPrice = fresh.dataset.cartTotalPrice || '';
      this.loadRecommendation();
      /* Focus was inside the replaced markup: put it back on the same line,
       * or on the first line when that line was removed. */
      if (hadFocus) {
        const target =
          (options.focusKey && this.querySelector(`[data-cart-qty-input][data-key="${CSS.escape(options.focusKey)}"]`)) ||
          this.querySelector('[data-cart-qty-input], a, button');
        target?.focus({ preventScroll: true });
      }
      return true;
    }

    announce(text) {
      const status = this.querySelector('[data-cart-status]');
      if (!status || !text) return;
      status.textContent = '';
      window.requestAnimationFrame(() => {
        status.textContent = text;
      });
    }

    showError(line, message) {
      const target = line
        ? this.querySelector(`.cart-line[data-line="${line}"] [data-line-error]`)
        : this.querySelector('[data-cart-error]');
      if (target) {
        target.textContent = message;
        target.hidden = false;
      }
      this.announce(message);
    }

    clearErrors() {
      this.querySelectorAll('[data-line-error], [data-cart-error]').forEach((el) => {
        el.hidden = true;
        el.textContent = '';
      });
    }

    async loadRecommendation() {
      const box = this.querySelector('[data-cart-recommendation][data-url]');
      if (!box || box.dataset.loaded) return;
      box.dataset.loaded = 'true';
      try {
        const response = await fetch(box.dataset.url, { headers: { Accept: 'text/html' } });
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('.shopify-section')?.innerHTML?.trim();
        if (inner) {
          box.innerHTML = inner;
          const heading = box.querySelector('[data-rec-heading]');
          if (heading) heading.textContent = box.dataset.heading || '';
          box.hidden = false;
        } else {
          box.hidden = true;
        }
      } catch {
        box.hidden = true;
      }
    }
  }

  if (!customElements.get('cart-form')) customElements.define('cart-form', CartForm);

  window.MatterCart = {
    add: (items, trigger) => primaryForm()?.add(items, trigger),
    addForm: (form, trigger) => primaryForm()?.addForm(form, trigger),
    refresh: () => primaryForm()?.refresh(),
    open: (trigger) => document.getElementById('CartDrawer')?.open(trigger),
  };
  // Quick add buttons anywhere on the page (product cards, recommendations).
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cart-add]');
    if (!button || button.closest('cart-form') || !primaryForm()) return;
    event.preventDefault();
    const id = Number(button.dataset.variantId);
    if (id) primaryForm().add([{ id, quantity: 1 }], button).catch(() => {});
  });
  document.addEventListener('cart:add', (event) => {
    const items = event.detail?.items || [];
    if (items.length) window.MatterCart.add(items, event.detail?.trigger)?.catch(() => {});
  });
  /* Back/forward cache: a page restored from memory may show a stale cart. */
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) window.MatterCart.refresh();
  });
})();
